import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./Gallery.css";

const GalleryEdit = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    media_file: null, // Changed to null for file upload
    media_type: "image",
  });
  const [currentMedia, setCurrentMedia] = useState({
    media_file: "",
    media_type: "image",
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchGalleryItem();
  }, [id]);

  const fetchGalleryItem = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.get(
        `https://ambatturindustrialestate.com/api/api/gallery-media/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCurrentMedia({
        media_file: response.data.media_file,
        media_type: response.data.media_type,
      });
      setFormData({
        media_file: null,
        media_type: response.data.media_type,
      });
    } catch (error) {
      console.error("Error fetching gallery item:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load gallery item",
      });
    } finally {
      setFetchLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (file) {
      setFormData((prev) => ({
        ...prev,
        media_file: file,
      }));

      // Auto-detect media type and create preview
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
        setFormData((prev) => ({ ...prev, media_type: "image" }));
      } else if (file.type.startsWith("video/")) {
        setPreview(URL.createObjectURL(file));
        setFormData((prev) => ({ ...prev, media_type: "video" }));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("access");

      if (formData.media_file) {
        // If new file is selected, use FormData for file upload
        const submitData = new FormData();
        submitData.append("media_file", formData.media_file);
        submitData.append("media_type", formData.media_type);

        await axios.patch(
          `https://ambatturindustrialestate.com/api/api/gallery-media/${id}/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // If no new file, just update the media type if changed
        if (formData.media_type !== currentMedia.media_type) {
          await axios.patch(
            `https://ambatturindustrialestate.com/api/api/gallery-media/${id}/`,
            { media_type: formData.media_type },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        }
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: formData.media_file
          ? "Media updated successfully"
          : "Media type updated successfully",
      });

      navigate("/gallery");
    } catch (error) {
      console.error("Error updating gallery item:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update gallery item",
      });
    } finally {
      setLoading(false);
    }
  };

  const removeNewFile = () => {
    setFormData((prev) => ({
      ...prev,
      media_file: null,
    }));
    setPreview(null);
  };

  if (fetchLoading) {
    return (
      <div className="gallery-container">
        <div className="gallery-loading">
          <div className="gallery-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <div className="gallery-content">
        <div className="gallery-form-container gallery-fade-in">
          <h1 className="gallery-form-title">Edit Gallery Item</h1>

          <form onSubmit={handleSubmit} className="gallery-form">
            {/* Current Media Preview */}
            <div className="gallery-form-group">
              <label className="gallery-form-label">Current Media</label>
              <div className="gallery-preview">
                {currentMedia.media_type === "image" ? (
                  <img
                    src={currentMedia.media_file}
                    alt="Current media"
                    className="gallery-preview-media"
                  />
                ) : (
                  <video
                    src={currentMedia.media_file}
                    className="gallery-preview-media"
                    controls
                  />
                )}
              </div>
              <p className="gallery-help-text">
                This is your current media file
              </p>
            </div>

            {/* New Media Upload Section */}
            <div className="gallery-form-group">
              <label className="gallery-form-label">
                Replace with New Media
              </label>

              {preview ? (
                <div className="gallery-new-preview-container">
                  <div className="gallery-preview">
                    {formData.media_type === "image" ? (
                      <img
                        src={preview}
                        alt="New media preview"
                        className="gallery-preview-media"
                      />
                    ) : (
                      <video
                        src={preview}
                        className="gallery-preview-media"
                        controls
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={removeNewFile}
                    className="gallery-remove-btn"
                  >
                    Remove New File
                  </button>
                  <p className="gallery-help-text success">
                    New file selected: {formData.media_file?.name}
                  </p>
                </div>
              ) : (
                <div
                  className={`gallery-upload-area ${
                    dragOver ? "drag-over" : ""
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() =>
                    document.getElementById("new_media_file").click()
                  }
                >
                  <div className="text-center">
                    <div className="gallery-empty-icon">🔄</div>
                    <p className="gallery-empty-text">
                      Click to upload new media or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      PNG, JPG, GIF, MP4, MOV up to 10MB
                    </p>
                  </div>
                  <input
                    id="new_media_file"
                    name="new_media_file"
                    type="file"
                    className="sr-only"
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                  />
                </div>
              )}
            </div>

            {/* Media Type Selection */}
            <div className="gallery-form-group">
              <label className="gallery-form-label">Media Type</label>
              <select
                value={formData.media_type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    media_type: e.target.value,
                  }))
                }
                className="gallery-form-select"
                disabled={!!formData.media_file} // Auto-detected when file is selected
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
              {formData.media_file && (
                <p className="gallery-help-text">
                  Media type auto-detected from file
                </p>
              )}
            </div>

            {/* Update Info */}
            <div className="gallery-update-info">
              <h4 className="gallery-info-title">Update Information:</h4>
              <ul className="gallery-info-list">
                <li>
                  •{" "}
                  {formData.media_file
                    ? "New file will replace current media"
                    : "Only media type will be updated"}
                </li>
                <li>• Media type: {formData.media_type}</li>
                {formData.media_file && (
                  <li>• File: {formData.media_file.name}</li>
                )}
              </ul>
            </div>

            {/* Buttons */}
            <div className="gallery-btn-group">
              <button
                type="button"
                onClick={() => navigate("/gallery")}
                className="gallery-btn gallery-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="gallery-btn gallery-btn-primary"
              >
                {loading ? (
                  <>
                    <div
                      className="gallery-spinner"
                      style={{
                        width: "16px",
                        height: "16px",
                        borderWidth: "2px",
                      }}
                    ></div>
                    Updating...
                  </>
                ) : formData.media_file ? (
                  "Replace Media"
                ) : (
                  "Update Media Type"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GalleryEdit;
